<template>
  <div ref="mapContainer" class="dealers-app__map"></div>
</template>

<script setup>
import { ref, onMounted, watch, toRaw, nextTick } from 'vue'
import { useGoogleMaps } from '../composables/useGoogleMaps';
import { useUserLocation } from '../composables/useUserLocation';

const props = defineProps({
  location: {
    type: Object,
    required: false,
    default: null
  }
})

// Get user location composable
const { userLocation } = useUserLocation();

const mapContainer = ref(null)
const map = ref(null)
const googleMaps = ref(null) // Store Google Maps reference
const markers = ref([]) // Array to hold all markers

// Function to clear all markers
const clearMarkers = () => {
  
  for (let i = 0; i < markers.value.length; i++) {
    if (markers.value[i] && markers.value[i].setMap) {
      markers.value[i].setMap(null);
    }
  }
  markers.value = [];
}

const addDealerToMap = async (dealer) => {

  
  // Check if map is initialized
  if (!map.value) {
    console.warn('Map not initialized yet, skipping addDealerToMap')
    return;
  }
  
  // Add null checks for nested properties
  if (!dealer?.location?.address) {

    return;
  }
  
  // Updated to use correct field names from API
  const { lat, lng } = dealer.location.address

  
  if (!lat || !lng) {

    return;
  }

  const latitude = parseFloat(lat)
  const longitude = parseFloat(lng)
  
  if (isNaN(latitude) || isNaN(longitude)) {

    return;
  }

  // Clear all existing markers first
  clearMarkers()


  const position = { lat: latitude, lng: longitude }


  // Center the map on the dealer location
  map.value.setCenter(position)
  map.value.setZoom(12) // Set a closer zoom level for individual dealer view


  try {
    // Create marker with more explicit options
    const markerOptions = {
      position: position,
      map: map.value,
      title: dealer?.dealerName || 'Unknown Dealer',
      animation: googleMaps.value.Animation?.DROP || null,
      visible: true,
      clickable: true
    }
    

    
    const marker = new googleMaps.value.Marker(markerOptions)
    
    // Verify marker was created successfully
    if (!marker) {
      console.error(`Failed to create marker`)
      return;
    }
    
    // Add marker to the array for tracking
    markers.value.push(marker)
    
  } catch (error) {
    console.error(`Error creating marker:`, error)
    console.error('Error details:', error.message, error.stack)
  }
}

watch(() => props.location, async (newLocation) => {
  
  if (newLocation && newLocation.location && newLocation.location.address) {
    await addDealerToMap(newLocation);
  } else {
    console.warn('No valid location provided to add to map')
  }
}, { immediate: true })

onMounted(async () => {
  
  try {
    googleMaps.value = await useGoogleMaps('AIzaSyABZyTkuDW5p7pO6cCGvmVrSU40s77Rs5A');
    
    // Determine initial map center
    const center = props.location?.location?.address
      ? {
          lat: parseFloat(props.location.location.address.lat || props.location.location.address.latitud || 0),
          lng: parseFloat(props.location.location.address.lng || props.location.location.address.longitude || 0)
        }
      : { lat: 39.5, lng: -98.35 } // Default center if no location provided
    
    let zoom = 4; // default zoom
    
 
    map.value = new googleMaps.value.Map(mapContainer.value, {
        center,
        zoom,
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: true,
        zoomControl: true
    });
    
    
    // Add initial dealer if provided
    if (props.location) {
      await addDealerToMap(props.location);
    }
  } catch (error) {
    console.error('Error loading Google Maps:', error)
  }
})
</script>

<style scoped>
:deep(.dealer-info-window) {
  max-width: 300px;
}

:deep(.dealer-info-window h3) {
  margin: 0 0 10px 0;
  color: #333;
  font-size: 16px;
}

:deep(.dealer-info-window p) {
  margin: 5px 0;
  font-size: 14px;
  color: #666;
}

:deep(.dealer-info-window a) {
  color: #e0457b;
  text-decoration: none;
}

:deep(.dealer-info-window a:hover) {
  text-decoration: underline;
}
</style>